/* eslint-disable functional/no-expression-statement,@typescript-eslint/ban-ts-comment */
import * as D3Selection from "d3-selection";

import * as Color from "~/Color";
import * as Log from "~/Log";
import { PairingChartData } from "~/Log/Chart/index";
import { getXScaledValue } from "~/Log/Chart/StatusEvents";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

const filterEvents = (
  pairingEvents: readonly PairingChartData[],
  chartTimeStart: Time.DateTime,
  chartTimeEnd: Time.DateTime
) => {
  const interval = Time.Interval.Stopped.fromDateTimes(
    chartTimeStart,
    chartTimeEnd
  );
  return pairingEvents.filter((e) =>
    pipe(
      interval,
      Time.Interval.Stopped.contains(Time.DateTime.fromNumber(e.start))
    )
  );
};

export const generate = ({
  svg,
  chartHeight,
  xScale,
  pairingEvents,
  defaultTimezone,
  chartTimeStart,
  chartTimeEnd,
}: Log.Chart.ChartGeneratorProps) => {
  const eventTip = D3Selection.select(".event-tip");
  const tipTitle = D3Selection.select(".tip-title");
  const tipContent = D3Selection.select(".tip-content");

  const topOffset: number = -chartHeight;

  const events = filterEvents(pairingEvents, chartTimeStart, chartTimeEnd);

  const pairingGroup = svg
    .append("g")
    .attr("class", "pairing-group")
    .attr("transform", `translate(0,${chartHeight})`);

  pairingGroup
    .selectAll("line.pairing-watermark")
    .data(events)
    .enter()
    .append("line")
    .attr("class", "pairing-watermark")
    .attr("x1", (pairingEvent: Log.Chart.PairingChartData) =>
      getXScaledValue(pairingEvent.start, xScale, defaultTimezone)
    )
    .attr("x2", (pairingEvent: Log.Chart.PairingChartData) =>
      getXScaledValue(pairingEvent.start, xScale, defaultTimezone)
    )
    .attr("y1", 40)
    .attr("y2", 6 + topOffset)
    .attr("fill", "none")
    .attr("shape-rendering", "crispEdges")
    .attr("stroke", Color.veryWhiteGrey())
    .attr("stroke-width", "2px")
    .attr("stroke-dasharray", "4 1");

  pairingGroup
    .selectAll("text.watermark-time")
    .data(events)
    .enter()
    .append("text")
    .attr("class", "watermark-time")
    .attr("text-anchor", "middle")
    .attr("fill", Color.steel())
    .attr("font-size", Typography.FontSize.D3.data())
    .attr("x", (d: Log.Chart.PairingChartData) =>
      getXScaledValue(d.start, xScale, defaultTimezone)
    )
    .attr("y", topOffset)
    .text((d: Log.Chart.PairingChartData) =>
      Time.DateTime.toHMMSS({ dateTime: d.start }, defaultTimezone)
    );

  pairingGroup
    .selectAll(".event-circle")
    .data(events)
    .enter()
    .append("circle")
    .attr("class", "event-circle")
    .attr("fill", Color.white())
    .attr("stroke", Color.veryWhiteGrey())
    .attr("stroke-width", "0.5px")
    .attr("r", 18)
    .attr("cx", (d: Log.Chart.PairingChartData) =>
      getXScaledValue(d.start, xScale, defaultTimezone)
    )
    .attr("cy", 40)
    .style("filter", "url(#drop-shadow)");

  pairingGroup
    .selectAll(".pairing-icon")
    .data(events)
    .enter()
    .append("svg")
    .attr("class", `pairing-icon ${Log.Chart.Icon.pairingSVGData.class}`)
    .attr("viewBox", Log.Chart.Icon.pairingSVGData.viewBox)
    .attr("width", Log.Chart.Icon.pairingSVGData.width)
    .attr("height", Log.Chart.Icon.pairingSVGData.height)
    .attr(
      "x",
      (pairingEvent: Log.Chart.PairingChartData) =>
        getXScaledValue(pairingEvent.start, xScale, defaultTimezone) - 9
    )
    .attr("y", 40 - Log.Chart.Icon.pairingSVGData.height / 2)
    .append("path")
    .attr("fill", Log.Chart.Icon.pairingSVGData.fill)
    // @ts-ignore
    .attr("d", Log.Chart.Icon.pairingSVGData.path)
    // @ts-ignore
    .on("mouseover", (event, d: PairingChartData) => {
      eventTip.transition().duration(200).style("opacity", 1);
      tipTitle.html(
        `<span><strong>${Time.DateTime.toHMMSS(
          { dateTime: d.start },
          defaultTimezone
        )}</strong></span>`
      );
      tipContent.html(
        `<div>&nbsp;Paired with&nbsp;<strong>${d.assetName}</strong></div>`
      );
      eventTip
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        .style("left", `${event.pageX + 12}px`)
        .style("top", `${event.pageY - 8}px`);
    })
    .on("mouseout", () => {
      eventTip.transition().duration(500).style("opacity", 0);
    });
};

export const update = ({
  svg,
  xScale,
  pairingEvents,
  chartTimeStart,
  chartTimeEnd,
  defaultTimezone,
}: Log.Chart.ChartGeneratorProps): void => {
  const events = filterEvents(pairingEvents, chartTimeStart, chartTimeEnd);

  svg
    .selectAll(".pairing-watermark")
    .data(events)
    .attr("x1", (pairingEvent: Log.Chart.PairingChartData) =>
      getXScaledValue(pairingEvent.start, xScale, defaultTimezone)
    )
    .attr("x2", (pairingEvent: Log.Chart.PairingChartData) =>
      getXScaledValue(pairingEvent.start, xScale, defaultTimezone)
    );

  svg
    .selectAll(".watermark-time")
    .data(events)
    .attr("x", (d: Log.Chart.PairingChartData) =>
      getXScaledValue(d.start, xScale, defaultTimezone)
    );

  svg
    .selectAll(".event-circle")
    .data(events)
    .attr("cx", (d: Log.Chart.PairingChartData) =>
      getXScaledValue(d.start, xScale, defaultTimezone)
    );

  svg
    .selectAll(".pairing-icon")
    .data(events)
    .attr(
      "x",
      (pairingEvent: Log.Chart.PairingChartData) =>
        getXScaledValue(pairingEvent.start, xScale, defaultTimezone) - 9
    );
};
